import { useSelector } from "react-redux";
import {
  useGetDesignBankListDataQuery,
  useGetDesignBankBasicDataQuery,
} from "../../../store/queries/Master";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/Master/DesignBank/designBankSlice";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { deleteDesignBank, downloadDesignBankData, getDesignBankData } from "./api";
import { toast } from "react-toastify";

const useDesignBank = () => {
  let paginationOptions = [
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
  ];

  const dispatch = useDispatch();
  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const queryState = useSelector((state) => state.designBank);
  const {
    data: queryList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetDesignBankListDataQuery({
    page_size: queryState.currentPageSize,
    page: queryState.currentPage,
    sort_by: queryState.sortBy,
    sort_order: queryState.sortOrder,
    start: queryState.startDate,
    end: queryState.endDate,
    search: queryState.search,
    supplier_id: queryState.supplier_id,
    item_id: queryState.item_id,
  });
  const { data: basicData = {} } = useGetDesignBankBasicDataQuery();

  const fieldsWithAction = useMemo(() => {
    const listFields = queryList?.data?.fields || {};

    if (listFields?.action) {
      return listFields;
    }

    return {
      ...listFields,
      action: {
        label: "Action",
        hidden: false,
        sortable: false,
      },
    };
  }, [queryList?.data?.fields]);

  useEffect(() => {
    if (
      JSON.stringify(tableFields || {}) === JSON.stringify(fieldsWithAction || {})
    ) {
      return;
    }

    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = fieldsWithAction;
      })
    );
  }, [dispatch, fieldsWithAction, tableFields]);

   useEffect(() => {
      if (queryState?.selectedItem && queryState?.isEdit) {
        fillDesignBankData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      queryState?.isEdit,
      queryState?.showAddBranchModal,
      queryState?.selectedItem,
    ]);

  useEffect(() => {
    if (queryState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [queryState.clearSelection]);

  const handleModalClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showAddModal = !state.showAddModal;
        state.isEdit = false;
        state.itemData = [];
      })
    );
  };

  const getRow = (field, data) => {
    const rows = {
      // Check for image field
      image: (field, data) =>
        data?.[field] ? (
          <img src={data?.[field]} alt="Image" width="200" height="200" className="image-class" />
        ) : (
          <p className="pro-mb-0">No image available</p>
        ),
      action: (field, data) => (
        <button
          type="button"
          className="pro-btn pro-btn-secondary"
          onClick={() => handleDownload(data)}
          disabled={queryState?.downloadingId === data?._id}
        >
          {queryState?.downloadingId === data?._id ? (
            <div className="spinner-border spinner-border-sm" role="status"></div>
          ) : (
            <span className="material-symbols-outlined">download</span>
          )}
        </button>
      ),
      common: (field, data) => <p className="pro-mb-0">{data?.[field]}</p>,
    };

    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleDownload = (rowData) => {
    const designBankId = rowData?._id;
    if (!designBankId) {
      toast.error("Design bank id not found");
      return;
    }

    dispatch(
      updateConfig((state) => {
        state.downloadingId = designBankId;
      })
    );

    downloadDesignBankData(designBankId)
      .then((response) => {
        if (!response?.data) {
          toast.error("Something went wrong");
          return;
        }

        const dataType = response?.data?.type || "application/octet-stream";
        const binaryData = [response.data];
        const downloadLink = document.createElement("a");
        const fileURL = window.URL.createObjectURL(
          new Blob(binaryData, { type: dataType })
        );
        const disposition = response?.headers?.["content-disposition"] || "";
        const matchedName = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        const fileName =
          matchedName?.[1]?.replace(/['"]/g, "") ||
          `${rowData?.design_id || "design_bank"}`;

        downloadLink.href = fileURL;
        downloadLink.setAttribute("download", fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        window.URL.revokeObjectURL(fileURL);
      })
      .finally(() => {
        dispatch(
          updateConfig((state) => {
            state.downloadingId = "";
          })
        );
      });
  };

  const handleDelete = () => {
    let data = {
      design_bank_id: queryState?.selectedItem,
    };
    deleteDesignBank(data).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success("Item deleted successfully");
        handleDeleteModal();
        refetch();
      } else {
        toast.error("Something went wrong");
        handleDeleteModal();
      }
    });
  };

    const fillDesignBankData = () => {
        getDesignBankData(queryState?.selectedItem).then((response) => {
        if (response?.data?.status_code === 200) {
          dispatch(
            updateConfig((state) => (state.itemData = response?.data?.data))
          );
        } else {
          toast.error("Something went wrong to fetch brach data");
        }
      });
    };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedItem = e?.[0];
      })
    );
  };

  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showAddModal = true;
        state.selectedItem = e?.[0];
        state.isEdit = true;
      })
    );
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };
  const handleSupplierFilter = (value) => {
    dispatch(
      updateConfig((state) => {
        state.supplier_id = value;
        state.currentPage = 1;
      })
    );
  };
  const handleItemTypeFilter = (value) => {
    dispatch(
      updateConfig((state) => {
        state.item_id = value;
        state.currentPage = 1;
      })
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(updateConfig((state) => {
        state.search = "";
        state.supplier_id = "";
        state.item_id = "";
    }));
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };
  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handleSort = (label) => {
    if (queryState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = queryState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };
  return {
    queryState,
    showEditModal,
    tableFields,
    queryList,
    isLoading,
    isFetching,
    paginationOptions,
    getRow,
    handleEditColumnsClick,
    handleSort,
    handlePagination,
    handleModalClick,
    handleDelete,
    handleDeleteModal,
    handleEditAction,
    handleSearch,
    handleSupplierFilter,
    handleItemTypeFilter,
    handleClearClick,
    handlePageSize,
    refetch,
    basicData: basicData?.data,
  };
};

export default useDesignBank;
